import { useMemo } from "react";
import usePlans from "@/hooks/usePlans";
import { makeTabTypeFromPlan } from "@/components/newcustomize/utils/makeProxyTabType";
import { PlanType } from "@/types/PlanTypes";
import { PRICING_PLAN_CATALOG } from "@/config/pricingPlanCatalog";
import sortPlansByRecency from "@/utils/sortPlansByRecency";
import isCancelledVerifiedPlan from "@/utils/isCancelledVerifiedPlan";

export type DashboardProductType =
  | "free"
  | "datacenter"
  | "staticResidential"
  | "residential"
  | "verified";

export type DashboardProductStatus = "active" | "cancelled" | "inactive";

export type DashboardProduct = {
  type: DashboardProductType;
  title: string;
  icon: string;
  plan?: PlanType;
  status: DashboardProductStatus;
};

const PRODUCT_DETAILS: Record<
  DashboardProductType,
  { title: string; icon: string }
> = {
  free: { title: "Free", icon: "/free-product-icon.svg" },
  datacenter: PRICING_PLAN_CATALOG.datacenter,
  staticResidential: PRICING_PLAN_CATALOG.staticResidential,
  residential: PRICING_PLAN_CATALOG.residential,
  verified: PRICING_PLAN_CATALOG.verified,
};

// A paused plan still has an overview to show, so it lands in the "active"
// bucket rather than the purchase flows.
function toProductStatus(plan: PlanType | undefined): DashboardProductStatus {
  if (!plan) return "inactive";
  return plan.status === "cancelled" ? "cancelled" : "active";
}

// One plan per product type, active plans prioritized, then most recent.
function buildPlanByType(plans: PlanType[]) {
  const planByType = new Map<string, PlanType>();
  const sortedPlans = sortPlansByRecency(plans);
  for (const pass of ["active", "any"] as const) {
    sortedPlans.forEach((plan) => {
      const type =
        plan.proxy_type === "free" ? "free" : makeTabTypeFromPlan(plan);
      if (pass === "active" && plan.status !== "active") return;
      if (!planByType.has(type)) planByType.set(type, plan);
    });
  }
  return planByType;
}

function buildProducts(planByType: Map<string, PlanType>): DashboardProduct[] {
  const hasActiveFreePlan = planByType.get("free")?.status === "active";
  const verifiedPlan = planByType.get("verified");
  const holdsVerifiedPlan =
    !!verifiedPlan && !isCancelledVerifiedPlan(verifiedPlan);

  const types: DashboardProductType[] = [
    ...(hasActiveFreePlan ? (["free"] as const) : []),
    "datacenter",
    "staticResidential",
    "residential",
    ...(holdsVerifiedPlan ? (["verified"] as const) : []),
  ];

  return types.map((type) => {
    const plan = planByType.get(type);
    return {
      type,
      ...PRODUCT_DETAILS[type],
      plan,
      status: toProductStatus(plan),
    };
  });
}

function pickDefaultProduct(products: DashboardProduct[]) {
  return (
    products.find((p) => p.type !== "free" && p.status === "active") ??
    products.find((p) => p.status === "active") ??
    products[0]
  );
}

/**
 * The product tab list for the dashboard product navigation (r-1776):
 * - The Free tab only exists while the user has an active free plan.
 * - The three standard paid products always get a tab (Active / Canceled /
 *   Inactive per the most relevant plan of that type, active prioritized).
 * - Verified is a legacy product with no purchase flow on the dashboard, so
 *   its tab only appears while the user holds a verified plan: a cancelled one
 *   has no Buy Again to offer, so it gets no tab (R-2272).
 * - The default selection is the first active paid product in tab order,
 *   falling back to the free tab, then the first tab.
 */
export default function useDashboardProducts() {
  const { data: allPlans, isLoading } = usePlans();

  return useMemo(() => {
    const products = buildProducts(buildPlanByType(allPlans?.results ?? []));
    return {
      isLoading,
      products,
      defaultProductType: pickDefaultProduct(products)?.type,
    };
  }, [allPlans, isLoading]);
}
